import {clsx} from 'clsx';
import {Collapsible as CollapsiblePrimitive, Slot as RadixSlot} from 'radix-ui';
import {memo} from 'react';
import type {ComponentPropsWithoutRef} from 'react';

import {Icon} from '../icon/index.js';
import * as styles from './style.css.js';

type CollapsibleRootProps = ComponentPropsWithoutRef<
  typeof CollapsiblePrimitive.Root
>;

const CollapsibleRoot = memo(function CollapsibleRoot({
  children,
  defaultOpen = true,
  ...props
}: CollapsibleRootProps) {
  return (
    <CollapsiblePrimitive.Root defaultOpen={defaultOpen} {...props}>
      {children}
    </CollapsiblePrimitive.Root>
  );
});

type CollapsibleAnchorProps = {
  label?: string;
  asChild?: boolean;
} & ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Trigger>;

const CollapsibleAnchor = memo(function CollapsibleAnchor({
  label,
  asChild = false,
  className,
  children,
  ...props
}: CollapsibleAnchorProps) {
  if (asChild) {
    return (
      <CollapsiblePrimitive.Trigger asChild {...props}>
        <RadixSlot.Slot className={className}>{children}</RadixSlot.Slot>
      </CollapsiblePrimitive.Trigger>
    );
  }

  return (
    <CollapsiblePrimitive.Trigger asChild {...props}>
      <button className={clsx(styles.anchor, className)}>
        {label && <span className={styles.anchorLabel}>{label}</span>}
        <Icon
          name="down"
          className={clsx(styles.anchorIcon, styles.anchorIconClosed)}
        />
        <Icon
          name="up"
          className={clsx(styles.anchorIcon, styles.anchorIconOpen)}
        />
      </button>
    </CollapsiblePrimitive.Trigger>
  );
});

type CollapsibleContentProps = ComponentPropsWithoutRef<
  typeof CollapsiblePrimitive.Content
>;

const CollapsibleContent = memo(function CollapsibleContent({
  className,
  children,
  ...props
}: CollapsibleContentProps) {
  return (
    <CollapsiblePrimitive.Content
      className={clsx(styles.content, className)}
      {...props}
    >
      <div className={styles.contentInner}>{children}</div>
    </CollapsiblePrimitive.Content>
  );
});

export const Collapsible = Object.assign(CollapsibleRoot, {
  Anchor: CollapsibleAnchor,
  Content: CollapsibleContent,
});

export type {
  CollapsibleRootProps,
  CollapsibleAnchorProps,
  CollapsibleContentProps,
};
